import { E as EPS, v as vop, s as implementsFunction, Y as compileHOF, V as compileG, w as compile, x as DOT, Z as DOT_G, t as defMathOp, l as defOp, o as ARGS_VVN, _ as MIX_N, q as defMathOpN, a0 as SET_N } from './codegen-df7bb22c.js';
import { d as distSq, i as illegalArgs } from './distsq-d74f45eb.js';
import { c as magSq, s as set } from './set-c051c4f9.js';
import { c as add } from './add-5cc35f4d.js';

const mi = -Infinity;
const mx = Infinity;
const MIN2 = Object.freeze([mi, mi]);
const MAX2 = Object.freeze([mx, mx]);
const ONE2 = Object.freeze([1, 1]);
const ZERO2 = Object.freeze([0, 0]);
const X2 = Object.freeze([1, 0]);
const Y2 = Object.freeze([0, 1]);
const MIN3 = Object.freeze([mi, mi, mi]);
const MAX3 = Object.freeze([mx, mx, mx]);
const ONE3 = Object.freeze([1, 1, 1]);
const ZERO3 = Object.freeze([0, 0, 0]);
const X3 = Object.freeze([1, 0, 0]);
const Y3 = Object.freeze([0, 1, 0]);
const Z3 = Object.freeze([0, 0, 1]);
const MIN4 = Object.freeze([mi, mi, mi, mi]);
const MAX4 = Object.freeze([mx, mx, mx, mx]);
const ONE4 = Object.freeze([1, 1, 1, 1]);
const ZERO4 = Object.freeze([0, 0, 0, 0]);
const X4 = Object.freeze([1, 0, 0, 0]);
const Y4 = Object.freeze([0, 1, 0, 0]);
const Z4 = Object.freeze([0, 0, 1, 0]);
const W4 = Object.freeze([0, 0, 0, 1]);

const sign = (x, eps = EPS) => (x > eps ? 1 : x < -eps ? -1 : 0);

const abs = Math.abs;
/**
 * Similar to {@link eqDeltaScaled}, but uses given `eps` as is.
 *
 * @param a - left value
 * @param b - right value
 * @param eps - epsilon / tolerance, default `1e-6`
 */
const eqDelta = (a, b, eps = EPS) => abs(a - b) <= eps;

/**
 * Clamps value `x` to given closed interval.
 *
 * @param x - value to clamp
 * @param min - lower bound
 * @param max - upper bound
 */
const clamp = (x, min, max) => (x < min ? min : x > max ? max : x);
/**
 * Clamps value `x` to closed [0 .. 1] interval.
 *
 * @param x
 */
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
/**
 * Returns the non-zero minimum value of the given `a`, `b` args.
 *
 * @param a
 * @param b
 */
const minNonZero2 = (a, b) => a !== 0 ? (b !== 0 ? Math.min(a, b) : a) : b;
/**
 * Returns the non-zero minimum value of the given `a`, `b`, `c` args.
 *
 * @param a
 * @param b
 * @param c
 */
const minNonZero3 = (a, b, c) => minNonZero2(minNonZero2(a, b), c);
/**
 * Returns true iff `x` is in closed interval `[min .. max]`
 *
 * @param x -
 * @param min -
 * @param max -
 */
const inRange = (x, min, max) => x >= min && x <= max;

const $ = (dim) => eqDelta$1.add(dim, compileHOF(dim, [eqDelta, EPS], ([a, b]) => `eq(${a},${b},eps)`, "eq,_eps", "a,b,eps=_eps", "a,b", "", "&&", "return a.length === b.length && ", ";"));
/**
 * Checks given vectors for componentwise equality, taking tolerance
 * `eps` (default: {@link @thi.ng/math#EPS}) into account.
 *
 * @param a
 * @param b
 * @param eps
 */
const eqDelta$1 = vop();
eqDelta$1.default((v1, v2, eps = EPS) => {
    if (implementsFunction(v1, "eqDelta")) {
        return v1.eqDelta(v2, eps);
    }
    if (implementsFunction(v2, "eqDelta")) {
        return v2.eqDelta(v1, eps);
    }
    return eqDeltaS(v1, v2, v1.length, eps);
});
const eqDelta2 = $(2);
const eqDelta3 = $(3);
const eqDelta4 = $(4);
/**
 * Checks given strided vectors for componentwise equality, taking
 * tolerance `eps` (default: {@link @thi.ng/math#EPS}) into account.
 *
 * @param a - first vector
 * @param b - second vector
 * @param n - number of elements
 * @param eps - tolerance
 * @param ia - start index a
 * @param ib - start index b
 * @param sa - stride a
 * @param sb - stride b
 */
const eqDeltaS = (a, b, n, eps = EPS, ia = 0, ib = 0, sa = 1, sb = 1) => {
    for (; n > 0; n--, ia += sa, ib += sb) {
        if (!eqDelta(a[ia], b[ib], eps)) {
            return false;
        }
    }
    return true;
};
const isInArray = (p, pts, eps = EPS) => {
    for (let i = pts.length; --i >= 0;) {
        if (eqDelta$1(p, pts[i], eps)) {
            return true;
        }
    }
    return false;
};

const $$1 = (dim) => dot.add(dim, compile(dim, DOT, "a,b", undefined, "", "+", "return ", ";"));
const dot = vop();
dot.default(compileG(DOT_G, "a,b", undefined, "s", "let s=0;"));
const dot2 = $$1(2);
const dot3 = $$1(3);
const dot4 = $$1(4);

const [sub, sub2, sub3, sub4] = defMathOp("-");

const [mixN, mixN2, mixN3, mixN4] = defOp(MIX_N, ARGS_VVN);

/**
 * Returns 2x the signed area of the 2D triangle defined by the given points.
 *
 * @remarks
 * This is a useful classifier for many geometry processing tasks. In addition
 * to the triangle area, the result can also be interpreted as follows:
 *
 * - `> 0`: points are ordered counterclockwise
 * - `< 0`: points are ordered clockwise
 * - `0`: points are co-linear
 *
 * Functionally same as: `cross2(sub2([], b, a), sub2([], c, a))`
 *
 * - {@link corner2}
 * - {@link clockwise2}
 * - {@link cross2}
 *
 * @param a -
 * @param b -
 * @param c -
 */
const signedArea2 = (a, b, c) => {
    const ax = a[0];
    const ay = a[1];
    return (b[0] - ax) * (c[1] - ay) - (c[0] - ax) * (b[1] - ay);
};

/**
 * Syntax sugar for `sign(signedArea2(a, b, c))`.
 *
 * - {@link @thi.ng/math#sign}
 * - {@link signedArea2}
 *
 * @param a -
 * @param b -
 * @param c -
 * @param eps - zero tolerance for sign computation
 */
const corner2 = (a, b, c, eps = EPS) => sign(signedArea2(a, b, c), eps);
/**
 * Returns true, if the triangle `a`, `b`, `c` is in clockwise order,
 * i.e. if `corner2(a,b,c)` returned -1.
 *
 * @param a -
 * @param b -
 * @param c -
 * @param eps -
 */
const clockwise2 = (a, b, c, eps = EPS) => corner2(a, b, c, eps) < 0;

const [divN, divN2, divN3, divN4] = defMathOpN("/");

const [setN, setN2, setN3, setN4] = defOp(SET_N, "a,n", "a", "a", 0, "");
const zeroes = (n) => new Array(n).fill(0);

const empty = (v) => implementsFunction(v, "empty") ? v.empty() : zeroes(v.length);

var IntersectionType;
(function (IntersectionType) {
    IntersectionType[IntersectionType["NONE"] = 0] = "NONE";
    IntersectionType[IntersectionType["PARALLEL"] = 1] = "PARALLEL";
    IntersectionType[IntersectionType["COINCIDENT"] = 2] = "COINCIDENT";
    IntersectionType[IntersectionType["COINCIDENT_NO_INTERSECT"] = 3] = "COINCIDENT_NO_INTERSECT";
    IntersectionType[IntersectionType["INTERSECT"] = 4] = "INTERSECT";
    IntersectionType[IntersectionType["INTERSECT_OUTSIDE"] = 5] = "INTERSECT_OUTSIDE";
})(IntersectionType || (IntersectionType = {}));

/**
 * Computes the parametric distance `t` of point `p` projected onto line
 * `a` -> `b`, relative to `a`. I.e. the projection of `p` can then be
 * computed like so:
 *
 * @example
 * ```ts
 * mixN([], a, b, closestT(p, a, b))
 * ```
 *
 * If the return value is outside the closed [0,1] interval, the
 * projected point lies outside the line segment. Returns `undefined` if
 * `a` and `b` are coincident.
 *
 * - {@link closestPointLine}
 * - {@link closestPointSegment}
 *
 * @param p - query point
 * @param a - line point A
 * @param b - line point B
 */
const closestT = (p, a, b) => {
    const d = sub([], b, a);
    const l = magSq(d);
    return l > 1e-6 ? dot(sub([], p, a), d) / l : undefined;
};
/**
 * Returns closest point to `p` on line segment `a` -> `b`. By default,
 * if the result point lies outside the segment, returns a copy of the
 * closest end point. The result is written to the optional `out` vector
 * (or if omitted, a new one is created).
 *
 * If `insideOnly` is true, only returns the closest point iff it
 * actually is inside the segment. The behavior of this configurable via
 * the optional `eps` arg and by default includes both end points. This
 * function uses {@link closestT} to compute the parametric position of the
 * result point and determine if it lies within the line segment. If
 * `eps > 0`, the end points `a` and `b` will be excluded from the
 * match, effectively shortening the valid line segment from both ends,
 * i.e. the valid interval of the parametric position will be
 * [eps,1-eps]. If the result lies outside this interval, the function
 * returns `undefined`. Likewise, if `a` and `b` are coincident.
 *
 * @param p - query point
 * @param a - line point A
 * @param b - line point B
 * @param out - result
 * @param eps - epsilon value
 */
const closestPointSegment = (p, a, b, out, insideOnly = false, eps = 0) => {
    const t = closestT(p, a, b);
    if (t !== undefined && (!insideOnly || (t >= eps && t <= 1 - eps))) {
        out = out || empty(p);
        return t <= 0 ? set(out, a) : t >= 1 ? set(out, b) : mixN(out, a, b, t);
    }
};
const closestPointPolyline = (p, pts, closed = false, out = []) => {
    if (!pts.length)
        return;
    const tmp = [];
    const n = pts.length - 1;
    let minD = Infinity, i, j;
    if (closed) {
        i = n;
        j = 0;
    }
    else {
        i = 0;
        j = 1;
    }
    for (; j <= n; i = j, j++) {
        if (closestPointSegment(p, pts[i], pts[j], tmp)) {
            const d = distSq(p, tmp);
            if (d < minD) {
                minD = d;
                set(out, tmp);
            }
        }
    }
    return minD < Infinity ? out : undefined;
};
/**
 * Returns the index of the start point containing the segment in the
 * polyline array `points` farthest away from `p` with regards to the
 * line segment `a` to `b`. `points` is only checked between indices
 * `from` and `to` (not including the latter).
 *
 * @param a - line point A
 * @param b - line point B
 * @param points - points
 * @param from - start search index
 * @param to - end search index
 */
const farthestPointSegment = (a, b, points, from = 0, to = points.length) => {
    let maxD = -1;
    let maxIdx = -1;
    const tmp = empty(a);
    for (let i = from; i < to; i++) {
        const p = points[i];
        const d = distSq(p, closestPointSegment(p, a, b, tmp) || a);
        if (d > maxD) {
            maxD = d;
            maxIdx = i;
        }
    }
    return [maxIdx, Math.sqrt(maxD)];
};

const centroid = (pts, out) => {
    const num = pts.length;
    !num && illegalArgs("no points");
    !out && (out = empty(pts[0]));
    for (let i = num; --i >= 0;) {
        add(out, out, pts[i]);
    }
    return divN(out, out, num);
};

/**
 * Computes and returns the center of the circumcircle of the given 2D
 * triangle points. Returns `undefined` if the points are colinear or
 * coincident.
 *
 * @param a - triangle vertex 1
 * @param b - triangle vertex 2
 * @param c - triangle vertex 3
 * @param eps - epsilon value for colinear check
 */
const circumCenter2 = (a, b, c, eps = EPS) => {
    const ax = a[0], ay = a[1];
    const bx = b[0], by = b[1];
    const cx = c[0], cy = c[1];
    const bax = bx - ax;
    const bay = by - ay;
    const cbx = cx - bx;
    const cby = cy - by;
    const deltaAB = Math.abs(bay);
    const deltaBC = Math.abs(cby);
    // colinear check
    if ((deltaAB < eps && deltaBC < eps) ||
        (Math.abs(bax) < eps && Math.abs(cbx) < eps)) {
        return;
    }
    const abx2 = (ax + bx) / 2;
    const aby2 = (ay + by) / 2;
    const bcx2 = (bx + cx) / 2;
    const bcy2 = (by + cy) / 2;
    if (deltaAB < eps) {
        return [abx2, (-cbx / cby) * (abx2 - bcx2) + bcy2];
    }
    if (deltaBC < eps) {
        return [bcx2, (-bax / bay) * (bcx2 - abx2) + aby2];
    }
    let m1 = -bax / bay;
    let m2 = -cbx / cby;
    let mx1 = abx2;
    let my1 = aby2;
    let mx2 = bcx2;
    let my2 = bcy2;
    let xc = (m1 * mx1 - m2 * mx2 + my2 - my1) / (m1 - m2);
    let yc = deltaAB > deltaBC ? m1 * (xc - mx1) + my1 : m2 * (xc - mx2) + my2;
    return [xc, yc];
};

const pointInSegment = (p, a, b, eps = EPS) => {
    const t = closestT(p, a, b);
    return t !== undefined
        ? distSq(p, mixN([], a, b, clamp01(t))) < eps * eps
        : false;
};
const pointInCircle = (p, pos, r) => distSq(pos, p) <= r * r;
const classifyPointInCircle = (p, pos, r, eps = EPS) => sign(r * r - distSq(pos, p), eps);
const pointInCircumCircle = (a, b, c, d) => magSq(a) * signedArea2(b, c, d) -
    magSq(b) * signedArea2(a, c, d) +
    magSq(c) * signedArea2(a, b, d) -
    magSq(d) * signedArea2(a, b, c) >
    0;
const pointInTriangle2 = (p, a, b, c) => {
    const s = clockwise2(a, b, c) ? 1 : -1;
    return (s * signedArea2(a, c, p) >= 0 &&
        s * signedArea2(b, a, p) >= 0 &&
        s * signedArea2(c, b, p) >= 0);
};
const classifyPointInTriangle2 = (p, a, b, c, eps = EPS) => {
    const s = clockwise2(a, b, c) ? 1 : -1;
    return sign(Math.min(s * signedArea2(a, c, p), s * signedArea2(b, a, p), s * signedArea2(c, b, p)), eps);
};
const pointInPolygon2 = (p, pts) => {
    const n = pts.length - 1;
    const px = p[0];
    const py = p[1];
    let a = pts[n];
    let b = pts[0];
    let inside = 0;
    for (let i = 0; i <= n; a = b, b = pts[++i]) {
        inside = classifyPointPolyPair(px, py, a[0], a[1], b[0], b[1], inside);
    }
    return inside;
};
const classifyPointPolyPair = (px, py, ax, ay, bx, by, inside) => ((ay < py && by >= py) || (by < py && ay >= py)) && (ax <= px || bx <= px)
    ? inside ^ (ax + ((py - ay) / (by - ay)) * (bx - ax) < px ? 1 : 0)
    : inside;
const pointInBox = vop(0);
const pointInRect = pointInBox.add(2, ([x, y], pos, size) => x >= pos[0] &&
    x <= pos[0] + size[0] &&
    y >= pos[1] &&
    y <= pos[1] + size[1]);
const pointInAABB = pointInBox.add(3, ([x, y, z], pos, size) => x >= pos[0] &&
    x <= pos[0] + size[0] &&
    y >= pos[1] &&
    y <= pos[1] + size[1] &&
    z >= pos[2] &&
    z <= pos[2] + size[2]);
pointInBox.default((p, boxMin, boxSize) => {
    for (let i = p.length; --i >= 0;) {
        const x = p[i];
        const y = boxMin[i];
        if (x < y || x > y + boxSize[i])
            return false;
    }
    return true;
});
const pointInCenteredBox = vop(0);
const pointInCenteredRect = pointInCenteredBox.add(2, ([x, y], pos, size) => x >= pos[0] - size[0] &&
    x <= pos[0] + size[0] &&
    y >= pos[1] - size[1] &&
    y <= pos[1] + size[1]);
const pointInCenteredAABB = pointInCenteredBox.add(3, ([x, y, z], pos, size) => x >= pos[0] - size[0] &&
    x <= pos[0] + size[0] &&
    y >= pos[1] - size[1] &&
    y <= pos[1] + size[1] &&
    z >= pos[2] - size[2] &&
    z <= pos[2] + size[2]);
pointInCenteredBox.default((p, boxCenter, boxExtent) => {
    for (let i = p.length; --i >= 0;) {
        const x = p[i];
        const y = boxCenter[i];
        const z = boxExtent[i];
        if (x < y - z || x > y + z)
            return false;
    }
    return true;
});

const intersectLineLine = (a, b, c, d, eps = EPS) => {
    const bax = b[0] - a[0];
    const bay = b[1] - a[1];
    const dcx = d[0] - c[0];
    const dcy = d[1] - c[1];
    const acx = a[0] - c[0];
    const acy = a[1] - c[1];
    const det = dcy * bax - dcx * bay;
    let alpha = dcx * acy - dcy * acx;
    let beta = bax * acy - bay * acx;
    if (eqDelta(det, 0, eps)) {
        if (eqDelta(alpha, 0, eps) && eqDelta(beta, 0, eps)) {
            let isec = closestPointSegment(c, a, b, undefined, true) ||
                closestPointSegment(d, a, b, undefined, true);
            return {
                type: isec
                    ? IntersectionType.COINCIDENT
                    : IntersectionType.COINCIDENT_NO_INTERSECT,
                isec,
            };
        }
        return { type: IntersectionType.PARALLEL };
    }
    alpha /= det;
    beta /= det;
    const ieps = 1 - eps;
    return {
        type: eps < alpha && alpha < ieps && eps < beta && beta < ieps
            ? IntersectionType.INTERSECT
            : IntersectionType.INTERSECT_OUTSIDE,
        isec: mixN2([], a, b, alpha),
        alpha,
        beta,
        det,
    };
};

/**
 * Performs Liang-Barsky clipping of the line segment with endpoints
 * `a`, `b` against the clipping rect defined by `min` and `max`. The
 * optional `ca` and `cb` vectors can be given to store the result
 * (clipped points). If omitted creates new vectors. Returns a tuple of
 * `[ca, cb, a, b]`, where the latter two values represent the
 * normalized distances of the clipped points relative to original given
 * line segment. Returns `undefined` iff the line lies completely
 * outside the rect.
 *
 * - {@link https://en.wikipedia.org/wiki/Liang%E2%80%93Barsky_algorithm}
 * - {@link https://github.com/thi-ng/c-thing/blob/develop/src/geom/clip/liangbarsky.c}
 *
 * @param a - line endpoint
 * @param b - line endpoint
 * @param min - bbox min
 * @param max - bbox max
 * @param ca - result A
 * @param cb - result B
 */
const liangBarsky2 = (a, b, min, max, ca = [], cb = []) => {
    const res = liangBarsky2Raw(a[0], a[1], b[0], b[1], min[0], min[1], max[0], max[1]);
    if (!res)
        return;
    ca[0] = res[0];
    ca[1] = res[1];
    cb[0] = res[2];
    cb[1] = res[3];
    return [ca, cb, res[4], res[5]];
};
/**
 * Same as {@link liangBarsky2} but for non-vector arguments.
 *
 * @param ax
 * @param ay
 * @param bx
 * @param by
 * @param minx
 * @param miny
 * @param maxx
 * @param maxy
 */
const liangBarsky2Raw = (ax, ay, bx, by, minx, miny, maxx, maxy) => {
    const dx = bx - ax;
    const dy = by - ay;
    let alpha = 0;
    let beta = 1;
    const clip = (p, q) => {
        if (p < 0) {
            const r = q / p;
            if (r > beta) {
                return false;
            }
            if (r > alpha) {
                alpha = r;
            }
        }
        else if (p > 0) {
            const r = q / p;
            if (r < alpha) {
                return false;
            }
            if (r < beta) {
                beta = r;
            }
        }
        else if (q < 0) {
            return false;
        }
        return true;
    };
    return clip(-dx, ax - minx) &&
        clip(dx, maxx - ax) &&
        clip(-dy, ay - miny) &&
        clip(dy, maxy - ay)
        ? [
            alpha * dx + ax,
            alpha * dy + ay,
            beta * dx + ax,
            beta * dy + ay,
            alpha,
            beta,
        ]
        : undefined;
};

/**
 * Extended version of Sutherland-Hodgeman convex polygon clipping
 * supporting any convex boundary polygon (not only rects). Returns new
 * array of clipped vertices.
 *
 * {@link https://en.wikipedia.org/wiki/Sutherland%E2%80%93Hodgman_algorithm}
 *
 * @param pts - subject poly vertices
 * @param bounds - clipping boundary vertices
 * @param bc - pre-computed boundary centroid
 * @param eps - edge classification tolerance
 */
const sutherlandHodgeman = (pts, bounds, bc, eps = EPS) => {
    bc = bc || centroid(bounds);
    for (let ne = bounds.length, j = ne - 1, i = 0; i < ne; j = i, i++) {
        const clipped = [];
        const ca = bounds[j];
        const cb = bounds[i];
        const sign = corner2(ca, cb, bc, eps);
        for (let np = pts.length, k = np - 1, l = 0; l < np; k = l, l++) {
            const p = pts[k];
            const q = pts[l];
            const cqsign = corner2(ca, cb, q, eps);
            if (corner2(ca, cb, p, eps) === sign) {
                clipped.push(cqsign !== sign
                    ? intersectLineLine(ca, cb, p, q).isec
                    : q);
            }
            else if (cqsign === sign) {
                clipped.push(intersectLineLine(ca, cb, p, q).isec, q);
            }
        }
        if (clipped.length < 2) {
            return [];
        }
        pts = clipped;
    }
    return pts;
};

export { centroid as A, signedArea2 as B, MIN3 as C, MAX3 as D, divN as E, classifyPointInCircle as F, classifyPointInTriangle2 as G, sutherlandHodgeman as H, IntersectionType as I, minNonZero2 as J, minNonZero3 as K, intersectLineLine as L, MAX2 as M, pointInAABB as N, pointInCircle as O, pointInSegment as P, isInArray as Q, pointInRect as R, pointInTriangle2 as S, pointInCircumCircle as T, X2 as X, ZERO2 as Z, ZERO3 as a, sub3 as b, clamp01 as c, dot as d, MIN2 as e, eqDelta2 as f, sub2 as g, clamp as h, inRange as i, closestPointPolyline as j, closestPointSegment as k, closestT as l, mixN as m, eqDelta$1 as n, farthestPointSegment as o, mixN2 as p, circumCenter2 as q, corner2 as r, sub as s, eqDelta as t, dot3 as u, sign as v, pointInPolygon2 as w, liangBarsky2 as x, Z3 as y, zeroes as z };
